import { FetchedStory } from '@/lib/backend/getStory';
import type { Block, Brand } from 'backend';
import classNames from 'classnames/bind';
import { useTranslation } from 'next-i18next';
import { CSSProperties } from 'react';
import { Vid, useFonts } from 'ui';
import { Img } from '../Img';
import styles from './index.module.scss';

export interface CoverProps {
  className?: string;
  onClick?: (e: any) => void;
  href?: string;
  target?: string;
  brand: Brand;
  image?: Block;
  title?: string;
  subtitle?: string;
  loading?: 'lazy' | 'eager';
  passive?: boolean;
  stripped?: boolean;
}

type CropType = {
  x: number;
  y: number;
};

export const Cover = ({
  id,
  title = '',
  brand,
  label,
  cover_image,
  cover_video,
  cover_layout = 'RESPONSIVE',
  cover_theme,
  cover_alignment = 'CENTER',
  cover_position = 'CENTER',
  cover_typeface = 'sans',
  cover_text_scale = 1,
  cover_text_color,
  cover_button_label,
  cover_button_color,
  cover_button_background,
  cover_overlay = '#0000000',
  cover_settings_logo,
  cover_settings_title,
  cover_image_crop,
  href,
  target,
  onClick,
  image,
  subtitle,
  loading = 'lazy',
  passive,
  stripped,
  ...rest
}: FetchedStory & CoverProps) => {
  const { style, resources } = useFonts(brand ?? {}, cover_typeface);
  const { t } = useTranslation('common');
  const imageStyle = {} as CSSProperties;

  const cropData = cover_image_crop as CropType;

  if (cropData) {
    imageStyle.objectPosition = `${
      Math.abs(cropData.x ?? 0.5) * 100
    }% ${Math.abs(cropData.y ?? 0.5) * 100}%`;
  }

  const typeface_type = cover_typeface?.split(':')[0];

  // set classnames
  const cx = classNames.bind(styles)(
    'cover',
    `color-${cover_theme}`,
    `type-${
      typeface_type && ['sans', 'serif'].includes(typeface_type)
        ? typeface_type
        : 'custom'
    }`,
    `align-${cover_alignment?.toLowerCase()}`,
    `position-${cover_position?.toLowerCase()}`,
    `layout-${cover_layout?.toLowerCase()}`,
    { hideLogo: cover_settings_logo, ...rest },
    { passive, stripped },
  );

  // render
  return (
    <>
      {resources}
      <a
        className={cx}
        href={href}
        target={target}
        style={style}
        onClick={onClick}
        key={id}
      >
        <div className={styles.inside}>
          <div className={styles.image}>
            {!!cover_image && (
              <Img
                className={styles.imageImg}
                src={cover_image}
                type="background"
                alt=""
                priority
                fill
                style={imageStyle}
              />
            )}

            {!cover_image && !cover_video && (
              <div
                className={styles.imageFill}
                style={{
                  backgroundColor:
                    brand?.color ?? (cover_theme == 'DARK' ? '#000' : '#ccc'),
                }}
              />
            )}

            {!!cover_video && (
              <Vid
                className={styles.image}
                src={cover_video}
                autoPlay
                loop
                muted
                playsInline
              />
            )}

            {image && (
              <Img
                className={styles.imageImg}
                src={image}
                alt={title || ''}
                loading={loading}
                fill
                sizes="100vw"
              />
            )}

            <div
              className={styles.overlay}
              style={{ backgroundColor: cover_overlay ?? undefined }}
            />
          </div>

          <div
            className={styles.text}
            style={{ color: cover_text_color ?? undefined }}
          >
            {(!brand?.logo_white || cover_settings_logo) && (
              <span className={styles.textBrand}>{brand?.name}</span>
            )}
            {brand?.logo_white && !cover_settings_logo && (
              <span className={styles.textLogo}>
                <Img
                  className={styles.textLogoImage}
                  src={brand.logo_white}
                  fill
                  priority
                  alt={brand?.name ?? ''}
                />
              </span>
            )}
            {!cover_settings_title && (
              <div className={styles.textTitle}>
                <div
                  className={styles.textTitleSizer}
                  style={{ fontSize: `${cover_text_scale ?? 1}em` }}
                >
                  {!!label && (
                    <em>
                      <span>{label}</span>
                    </em>
                  )}
                  <span>{(title ?? '').replace(/\\n/g, '\n')}</span>
                </div>
              </div>
            )}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            <span className={styles.textGo}>
              <span
                className={styles.button}
                style={{
                  color: cover_button_color ?? brand?.color ?? '#000',
                  backgroundColor: cover_button_background ?? undefined,
                }}
              >
                <span>
                  {cover_button_label ??
                    t('open-cover', { lng: brand?.language ?? undefined })}
                </span>
              </span>
            </span>
          </div>
        </div>
      </a>
    </>
  );
};
